import * as React from 'react';
import { EMPTY_TEXT, LocaleCodes } from '@mitchell/react.component.ux';
import { TranslationService } from '@mitchell/redux.module.apd/dist/domain/common/services/TranslationService';
import { ConnectedGPCRootComponent } from '@mitchell/redux.module.apd/dist/app/guidedPhotoCapture/containers';
import { IGPCRootComponent } from '@mitchell/redux.module.apd';
import { GuidedPhotoCaptureShell } from './components';
import { createBrowserHistory } from 'history';
import { GPCLaunchMetadataQueryParams } from './constants';

const detectOSInfo = (): string => {
	const userAgent = window.navigator.userAgent || EMPTY_TEXT;
	const platform = window.navigator.platform || EMPTY_TEXT;

	if (/Windows/i.test(userAgent) || /Win/i.test(platform)) {
		return 'Windows';
	}

	if (/Android/i.test(userAgent)) {
		return 'Android';
	}

	if (/iPhone/i.test(userAgent)) {
		return 'iOS';
	}
	if (/iPhone|iPad|iPod/i.test(userAgent)) {
		return 'iOS';
	}
	if (/Mac/i.test(platform) || /Mac OS X/i.test(userAgent)) {
		return 'macOS';
	}

	if (/Linux/i.test(platform) || /Linux/i.test(userAgent)) {
		return 'Linux';
	}

	return platform || EMPTY_TEXT;
};

const detectDeviceInfo = (): string => {
	const userAgent = window.navigator.userAgent || EMPTY_TEXT;
	const platform = window.navigator.platform || EMPTY_TEXT;
	const maxTouchPoints = window.navigator.maxTouchPoints || 0;
	const isIPhoneOrIPod = /iPhone|iPod/i.test(userAgent);

	const isIPadDesktopMode =
		platform === 'MacIntel' && maxTouchPoints > 1 && /Macintosh/i.test(userAgent) && /Mobile/i.test(userAgent);

	if (isIPhoneOrIPod) {
		return 'Mobile';
	}

	if (/iPad/i.test(userAgent) || isIPadDesktopMode) {
		return 'iPad';
	}

	return /Mobile|Android/i.test(userAgent) ? 'Mobile' : 'Desktop';
};

const detectVersion = (): string => {
	const userAgent = window.navigator.userAgent || EMPTY_TEXT;
	const userAgentData = (window.navigator as Navigator & { userAgentData?: { platformVersion?: string } })
		.userAgentData;
	const isWindows = /Windows/i.test(userAgent);
	const isAppleMobileDevice = /iPhone|iPad|iPod/i.test(userAgent);
	const safariVersionMatch = userAgent.match(/Version\/([\d.]+).*Safari/i);

	const mapWindowsPlatformVersion = (platformVersion: string): string => {
		const majorVersion = Number.parseInt(platformVersion.split('.')[0], 10);
		if (!Number.isNaN(majorVersion)) {
			return majorVersion >= 13 ? '11' : '10';
		}

		return platformVersion.replace(/(?:\.0)+$/g, EMPTY_TEXT);
	};

	const androidVersionMatch = userAgent.match(/Android\s([\d.]+)/i);
	if (androidVersionMatch?.[1]) {
		return androidVersionMatch[1];
	}

	if (isWindows && userAgentData?.platformVersion) {
		return mapWindowsPlatformVersion(userAgentData.platformVersion);
	}

	if (/Android/i.test(userAgent) && userAgentData?.platformVersion) {
		return userAgentData.platformVersion.replace(/(?:\.0)+$/g, EMPTY_TEXT);
	}

	const iosVersionPatterns: RegExp[] = [
		/\b(?:iPhone|iPad|iPod)\s*OS\s([\d_]+)/i,
		/\bCPU(?:\s+iPhone)?\s+OS\s([\d_]+)/i,
		/OS\s([\d_]+)\slike Mac OS X/i,
	];

	for (const pattern of iosVersionPatterns) {
		const match = userAgent.match(pattern);
		if (match?.[1]) {
			const parsedIOSVersion = match[1].replace(/_/g, '.');

			if (isAppleMobileDevice && safariVersionMatch?.[1]) {
				const iosMajorVersion = Number.parseInt(parsedIOSVersion.split('.')[0], 10);
				const safariMajorVersion = Number.parseInt(safariVersionMatch[1].split('.')[0], 10);

				if (
					!Number.isNaN(iosMajorVersion) &&
					!Number.isNaN(safariMajorVersion) &&
					safariMajorVersion - iosMajorVersion >= 5
				) {
					return safariVersionMatch[1];
				}
			}

			return parsedIOSVersion;
		}
	}

	const osVersionPatterns: RegExp[] = [/Android\s([\d.]+)/i, /Windows NT\s([\d.]+)/i, /Mac OS X\s([\d_]+)/i];

	for (const pattern of osVersionPatterns) {
		const match = userAgent.match(pattern);
		if (match?.[1]) {
			return match[1].replace(/_/g, '.');
		}
	}

	const browserVersionPatterns: RegExp[] = [
		/CriOS\/([\d.]+)/i,
		/Chrome\/([\d.]+)/i,
		/FxiOS\/([\d.]+)/i,
		/Firefox\/([\d.]+)/i,
		/EdgA?\/([\d.]+)/i,
		/Version\/([\d.]+).*Safari/i,
	];

	if (isAppleMobileDevice) {
		if (safariVersionMatch?.[1]) {
			return safariVersionMatch[1];
		}

		return EMPTY_TEXT;
	}

	for (const pattern of browserVersionPatterns) {
		const match = userAgent.match(pattern);
		if (match?.[1]) {
			return match[1];
		}
	}

	return window.navigator.appVersion || EMPTY_TEXT;
};

const detectOSVersionWithHighEntropy = async (fallbackVersion: string): Promise<string> => {
	const userAgent = window.navigator.userAgent || EMPTY_TEXT;
	const isAndroid = /Android/i.test(userAgent);
	const isWindows = /Windows/i.test(userAgent);

	if (!isAndroid && !isWindows) {
		return fallbackVersion;
	}

	const mapWindowsPlatformVersion = (platformVersion: string): string => {
		const majorVersion = Number.parseInt(platformVersion.split('.')[0], 10);
		if (!Number.isNaN(majorVersion)) {
			return majorVersion >= 13 ? '11' : '10';
		}

		return platformVersion.replace(/(?:\.0)+$/g, EMPTY_TEXT);
	};

	const userAgentData = (
		window.navigator as Navigator & {
			userAgentData?: {
				getHighEntropyValues?: (hints: string[]) => Promise<{ platformVersion?: string }>;
			};
		}
	).userAgentData;

	if (!userAgentData?.getHighEntropyValues) {
		return fallbackVersion;
	}

	try {
		const highEntropyValues = await userAgentData.getHighEntropyValues(['platformVersion']);
		if (highEntropyValues.platformVersion) {
			if (isWindows) {
				return mapWindowsPlatformVersion(highEntropyValues.platformVersion);
			}

			return highEntropyValues.platformVersion.replace(/(?:\.0)+$/g, EMPTY_TEXT);
		}
	} catch (error) {
		return fallbackVersion;
	}

	return fallbackVersion;
};

const detectBrowserDetails = (): { browser: string; browserVersion: string } => {
	const userAgent = window.navigator.userAgent || EMPTY_TEXT;

	const browserPatterns: Array<{ name: string; pattern: RegExp }> = [
		{ name: 'Edge', pattern: /EdgA?\/([\d.]+)/i },
		{ name: 'Opera', pattern: /OPR\/([\d.]+)/i },
		{ name: 'Chrome', pattern: /CriOS\/([\d.]+)/i },
		{ name: 'Chrome', pattern: /Chrome\/([\d.]+)/i },
		{ name: 'Firefox', pattern: /FxiOS\/([\d.]+)/i },
		{ name: 'Firefox', pattern: /Firefox\/([\d.]+)/i },
		{ name: 'Safari', pattern: /Version\/([\d.]+).*Safari/i },
		{ name: 'Internet Explorer', pattern: /MSIE\s([\d.]+)/i },
		{ name: 'Internet Explorer', pattern: /Trident\/.*rv:([\d.]+)/i },
	];

	for (const browserPattern of browserPatterns) {
		const match = userAgent.match(browserPattern.pattern);
		if (match?.[1]) {
			return { browser: browserPattern.name, browserVersion: match[1] };
		}
	}

	return { browser: 'Unknown', browserVersion: EMPTY_TEXT };
};

const detectBrowserDetailsWithHighEntropy = async (): Promise<{ browser: string; browserVersion: string }> => {
	const fallback = detectBrowserDetails();
	const userAgentData = (
		window.navigator as Navigator & {
			userAgentData?: {
				getHighEntropyValues?: (
					hints: string[]
				) => Promise<{ fullVersionList?: Array<{ brand: string; version: string }>; uaFullVersion?: string }>;
			};
		}
	).userAgentData;

	if (!userAgentData?.getHighEntropyValues) {
		return fallback;
	}

	try {
		const highEntropyValues = await userAgentData.getHighEntropyValues(['fullVersionList', 'uaFullVersion']);
		const fullVersionList = highEntropyValues.fullVersionList || [];
		const preferredBrand = fullVersionList.find((brandVersion) =>
			/Prisma|Chrom(e|ium)|Edge|Firefox|Safari|Opera/i.test(brandVersion.brand)
		);

		if (preferredBrand?.version) {
			const normalizedBrowserName = /Prisma/i.test(preferredBrand.brand) ? 'Prisma Browser' : fallback.browser;
			return {
				browser: normalizedBrowserName,
				browserVersion: preferredBrand.version,
			};
		}

		if (highEntropyValues.uaFullVersion) {
			return {
				browser: fallback.browser,
				browserVersion: highEntropyValues.uaFullVersion,
			};
		}
	} catch (error) {
		return fallback;
	}

	return fallback;
};

const normalizeOSInfoValue = (osInfoValue: string): string => {
	const withoutVersionSegment = osInfoValue.replace(/,\s*OS\s+version\s*-\s*.+$/i, EMPTY_TEXT).trim();
	return withoutVersionSegment.replace(/^(?:OS\s*-\s*)+/i, EMPTY_TEXT).trim();
};

const extractOSVersionValue = (osInfoValue: string): string => {
	const versionMatch = osInfoValue.match(/(?:^|,)\s*OS\s+version\s*[-:]\s*(.+)$/i);
	return versionMatch?.[1]?.trim() || EMPTY_TEXT;
};

const App = (): React.ReactElement => {
	const getMicValue = (): boolean => {
		if (Array.from(urlParams.keys()).length > 0) {
			return urlParams.has('isMic') && urlParams.get('isMic') !== 'false';
		}
		return sessionStorage.getItem('isMic') === 'true';
	};
	const getShopSelect = (): boolean => {
		if (Array.from(urlParams.keys()).length > 0) {
			return urlParams.has('isShopSelect') && urlParams.get('isShopSelect') !== 'false';
		}
		return sessionStorage.getItem('isShopSelect') === 'true';
	};
	const getConfidenceValue = (): boolean => {
		if (Array.from(urlParams.keys()).length > 0) {
			return urlParams.has('isConfidence') && urlParams.get('isConfidence') !== 'false';
		}
		return sessionStorage.getItem('isConfidence') === 'true';
	};

	const shouldShowVinDecode = (): boolean => {
		if (Array.from(urlParams.keys()).length > 0) {
			return urlParams.has('isVinDecode') && urlParams.get('isVinDecode') !== 'false';
		}
		return sessionStorage.getItem('isVinDecode') === 'true';
	};

	const getLaunchMetadataValue = (metadataKey: string, fallbackGetter: () => string): string => {
		const urlValue = urlParams.get(metadataKey);
		if (urlValue) {
			return urlValue;
		}

		const sessionValue = sessionStorage.getItem(metadataKey);
		if (sessionValue) {
			return sessionValue;
		}

		return fallbackGetter();
	};

	const urlParams = new URLSearchParams(window.location.search);
	const coCode = urlParams.get('coCd') || urlParams.get('companyCode') || sessionStorage.getItem('coCd') || EMPTY_TEXT;
	const accessToken = urlParams.get('initData') || EMPTY_TEXT;
	const contextId = urlParams.get('id') || sessionStorage.getItem('id') || EMPTY_TEXT;
	const localLanguageCode = window.navigator.language.toLowerCase();
	const frenchLocaleCodes = [
		LocaleCodes.FR.toLowerCase(),
		LocaleCodes.FR_CA.toLowerCase(),
		LocaleCodes.FR_FR.toLowerCase(),
	];
	const isBrowserLanguageFrench = frenchLocaleCodes.includes(localLanguageCode);
	const languageFallback = isBrowserLanguageFrench ? LocaleCodes.FR_CA : LocaleCodes.EN_US;
	const language = urlParams.get('lang') || sessionStorage.getItem('lang') || languageFallback;

	const isMic: boolean = getMicValue();
	const isShopSelect: boolean = getShopSelect();
	const isVinDecode: boolean = shouldShowVinDecode();
	const isConfidence: boolean = getConfidenceValue();
	const launchDeviceTypeValue = getLaunchMetadataValue(GPCLaunchMetadataQueryParams.deviceType, detectDeviceInfo);
	const launchOSInfoValue = getLaunchMetadataValue(GPCLaunchMetadataQueryParams.osInfo, detectOSInfo);
	const launchBrowserInfoValue = getLaunchMetadataValue(GPCLaunchMetadataQueryParams.browserInfo, detectVersion);
	const launchOSVersionFallback = extractOSVersionValue(launchOSInfoValue) || detectVersion();
	const launchMetadata: {
		deviceType: string;
		osInfo: string;
		browserInfo: string;
	} = {
		deviceType: launchDeviceTypeValue,
		osInfo: normalizeOSInfoValue(launchOSInfoValue),
		browserInfo: launchBrowserInfoValue,
	};

	if (localStorage.getItem('accessToken') !== null) {
		localStorage.removeItem('accessToken');
	}

	const appProps: IGPCRootComponent = {
		companyCode: coCode,
		contextId: contextId,
		language: language,
		isMic: isMic,
		isConfidence: isConfidence,
		deviceType: launchMetadata.deviceType,
		osInfo: launchMetadata.osInfo,
		browserInfo: launchMetadata.browserInfo,
		history: createBrowserHistory(),
	};

	React.useEffect(() => {
		const showDeviceDetails = async (): Promise<void> => {
			const browserDetails = await detectBrowserDetailsWithHighEntropy();
			const resolvedOSVersion = await detectOSVersionWithHighEntropy(launchOSVersionFallback);
			const deviceDetails = {
				deviceType: launchMetadata.deviceType,
				osInfo: `OS - ${launchMetadata.osInfo}, OS version - ${resolvedOSVersion}`,
				browserInfo: `Browser - ${browserDetails.browser} ${browserDetails.browserVersion}`,
			};

			sessionStorage.setItem(GPCLaunchMetadataQueryParams.deviceType, deviceDetails.deviceType);
			sessionStorage.setItem(GPCLaunchMetadataQueryParams.osInfo, deviceDetails.osInfo);
			sessionStorage.setItem(GPCLaunchMetadataQueryParams.browserInfo, deviceDetails.browserInfo);
		};

		showDeviceDetails();
	}, [launchMetadata.deviceType, launchMetadata.osInfo, launchOSVersionFallback]);
	isShopSelect && sessionStorage.setItem('isShopSelect', String(true));
	isVinDecode && sessionStorage.setItem('isVinDecode', String(true));
	if (accessToken) {
		localStorage.setItem('accessToken', accessToken);
		sessionStorage.setItem('accessToken', accessToken);
	}
	sessionStorage.setItem('coCd', appProps.companyCode || EMPTY_TEXT);
	sessionStorage.setItem('id', appProps.contextId || EMPTY_TEXT);
	sessionStorage.setItem('lang', appProps.language);
	sessionStorage.setItem('isMic', String(appProps.isMic || false));
	sessionStorage.setItem('isConfidence', String(appProps.isConfidence || false));
	sessionStorage.setItem('i18nextLng', appProps.language);
	TranslationService.setCurrentLanguage(appProps.language);
	const renderComponent = React.useCallback(() => <ConnectedGPCRootComponent {...appProps} />, [appProps]);
	return <GuidedPhotoCaptureShell homePage={renderComponent} />;
};

export default App;
